import { useEffect, useState } from 'react';

import {
  applyPageSetupResult,
  getPageSetupDataKey,
  storePageSetupData,
} from '../components/PageBackgroundSync';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import {
  franchiseAuthHeaders,
  appendFranchiseThemeParams,
} from '../../shared/franchiseFlowAuth';
import { readStoredFranchiseBrandLogo } from '../../shared/franchiseBrand';

function readStoredLogo(token) {
  const key = getPageSetupDataKey(token);
  if (!key) return '';
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return '';
    const parsed = JSON.parse(stored);
    const logo = parsed?.external_fields?.org_logo;
    return typeof logo === 'string' ? logo : '';
  } catch {
    return '';
  }
}

function pageSetupUrl(token) {
  const base = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;
  const qs = appendFranchiseThemeParams({ _: String(Date.now()) });
  return `${base}?${qs.toString()}`;
}

/**
 * Fetch page-setup for org logo + apply existing skin theme tokens.
 */
export function usePageSetupLogo(sessionToken) {
  const [orgLogoUrl, setOrgLogoUrl] = useState('');

  useEffect(() => {
    const token = String(sessionToken || '').trim();
    if (!token) {
      setOrgLogoUrl('');
      return undefined;
    }

    const franchiseLogo = readStoredFranchiseBrandLogo();
    if (franchiseLogo) setOrgLogoUrl(franchiseLogo);

    const cachedLogo = readStoredLogo(token);
    if (cachedLogo) setOrgLogoUrl(cachedLogo);

    const dataKey = getPageSetupDataKey(token);
    try {
      const stored = dataKey ? sessionStorage.getItem(dataKey) : null;
      if (stored) applyPageSetupResult(JSON.parse(stored));
    } catch {
      // ignore parse errors
    }

    let cancelled = false;
    fetchWithTimeout(pageSetupUrl(token), {
      timeoutMs: 20000,
      cache: 'no-store',
      credentials: 'include',
      headers: franchiseAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result) return;
        applyPageSetupResult(result);
        storePageSetupData(token, result);
        const logo =
          result?.external_fields?.franchise_logo || result?.external_fields?.org_logo;
        if (typeof logo === 'string' && logo.trim()) {
          setOrgLogoUrl(logo.trim());
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [sessionToken]);

  return orgLogoUrl;
}
